import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { dayLabel, dayWithYear } from '../api/quotes.ts';
import {
  bookDock,
  clearDockBooking,
  docksToBookQuery,
  jobVenueWarningsQuery,
  quoteVenueWarningsQuery,
  type VenueWarning,
} from '../api/venue-warnings.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ VEN-02 at v1.973 (venue slice 1, part B2): what the venue's access says about this job or
// quote — the dock to book, delivery hours, the curfew, the goods lift — worded for the office. They
// are problems to sort out, so they read in red; none of them refuses anything.

// dayLabel already adds the year outside this one; these dates always carry it, once.
const withYear = (date: string) => dayWithYear(date);

/** One warning in the office's words. The dock is said by the job's own panel. */
export function warningText(w: VenueWarning): string {
  switch (w.kind) {
    case 'dock_to_book':
      return w.overdue
        ? `The dock should have been booked by ${withYear(w.dueOn ?? '')}`
        : `Book the dock by ${withYear(w.dueOn ?? '')}`;
    case 'outside_hours':
      return `${w.label} ${w.local}, outside delivery hours (${w.hours})`;
    case 'after_curfew':
      return `${w.label} ends ${w.local}, after the ${w.curfew} curfew`;
    case 'too_big_for_lift':
      return `${w.description} is too ${w.reason === 'weight' ? 'heavy' : 'big'} for the goods lift`;
  }
}

function WarningList({
  warnings,
  zone,
}: {
  warnings: readonly VenueWarning[];
  zone: string | null;
}) {
  if (warnings.length === 0) return null;
  return (
    <div className="alert flex flex-col gap-1" role="status">
      <span className="font-semibold">Getting in at the venue</span>
      <ul className="flex flex-col gap-0.5">
        {warnings.map((w, i) => (
          <li key={i}>{warningText(w)}</li>
        ))}
      </ul>
      {zone ? <span className="text-xs">Times are the venue’s, {zone}.</span> : null}
    </div>
  );
}

export function QuoteVenueWarnings({ quoteId }: { quoteId: string }) {
  const found = useQuery(quoteVenueWarningsQuery(quoteId));
  if (!found.data) return null;
  return <WarningList warnings={found.data.warnings} zone={found.data.zone ?? null} />;
}

/** The job's warnings, with the dock booking that answers the first of them. */
export function JobVenueWarnings({ jobId, mayManage }: { jobId: string; mayManage: boolean }) {
  const found = useQuery(jobVenueWarningsQuery(jobId));
  const queryClient = useQueryClient();
  const [booking, setBooking] = useState(false);
  const [reference, setReference] = useState('');
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: jobVenueWarningsQuery(jobId).queryKey });
    await queryClient.invalidateQueries({ queryKey: docksToBookQuery.queryKey });
  };
  const book = useMutation({
    mutationFn: () => bookDock(jobId, reference.trim() || null),
    onSuccess: async () => {
      setBooking(false);
      setReference('');
      await refresh();
    },
  });
  const clear = useMutation({ mutationFn: () => clearDockBooking(jobId), onSuccess: refresh });
  if (!found.data) return null;
  const { warnings, dockBooking, zone } = found.data;
  const dockDue = warnings.some((w) => w.kind === 'dock_to_book');

  function submit(event: FormEvent) {
    event.preventDefault();
    book.mutate();
  }

  return (
    <div className="flex flex-col gap-2">
      <WarningList warnings={warnings} zone={zone ?? null} />
      {dockBooking ? (
        <p className="notice flex flex-wrap items-center gap-2 text-sm">
          <span>
            Dock booked{dockBooking.reference ? `, reference ${dockBooking.reference}` : ''}.
          </span>
          {mayManage ? (
            <Button variant="quiet" disabled={clear.isPending} onClick={() => clear.mutate()}>
              Clear
            </Button>
          ) : null}
        </p>
      ) : null}
      {dockDue && mayManage && !booking ? (
        <div>
          <Button variant="quiet" onClick={() => setBooking(true)}>
            Dock booked…
          </Button>
        </div>
      ) : null}
      {booking ? (
        <form
          onSubmit={submit}
          className="flex max-w-md flex-col gap-2 border-2 border-divider p-3"
        >
          <Field
            label="The venue’s booking reference (optional)"
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
          />
          {book.error ? (
            <p role="alert" className="text-sm font-semibold text-accent-2-700">
              {book.error.message}
            </p>
          ) : null}
          <span className="flex gap-2">
            <Button type="submit" disabled={book.isPending}>
              Save
            </Button>
            <Button type="button" variant="quiet" onClick={() => setBooking(false)}>
              Cancel
            </Button>
          </span>
        </form>
      ) : null}
      {clear.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {clear.error.message}
        </p>
      ) : null}
    </div>
  );
}

/** For the home screen: jobs whose venue's dock is still to book, soonest due first. */
export function DocksToBook() {
  const docks = useQuery(docksToBookQuery);
  if (!docks.data || docks.data.length === 0) return null;
  return (
    <section aria-labelledby="docks" className="flex flex-col gap-3">
      <h2 id="docks" className="section-title">
        Docks to book
      </h2>
      <ul className="flex flex-col gap-2 text-sm">
        {docks.data.map((d) => (
          <li key={d.job.id} className="flex flex-wrap items-baseline gap-2">
            <Link to="/jobs/$jobId" params={{ jobId: d.job.id }} className="link font-semibold">
              J-{d.job.number} {d.job.name}
            </Link>
            <span className="text-ink-soft">at {d.venue.name}</span>
            <span className={d.overdue ? 'tag tag-accent-2' : 'tag'}>
              {d.overdue ? 'Overdue — was due' : 'By'} {withYear(d.dueOn)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
